import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from '../../store/store';
import { removeProductFromCart } from '../../store/UserSlice';
import { getCsrfToken } from '../../utils/helpers';

const YM_PURCHASE_SNAPSHOT_KEY = 'yoo_purchase_snapshot_v1';
const LOCAL_CART_STORAGE_KEY = 'cart';
const RECONCILE_INTERVAL_MS = 30_000;

type YmPurchaseProduct = {
    id: string;
    name: string;
    price?: number;
    quantity?: number;
};
type YmPurchaseSnapshot = {
    v: 1 | 2;
    orderNumber: string;
    paymentId: string;
    total: number | null;
    products: YmPurchaseProduct[];
    productIds?: string[];
    createdAt: number;
    userId?: string;
};
type CartLikeItem = {
    productId?: unknown;
    [key: string]: unknown;
};
type UserCartResponse = {
    cart?: unknown;
};

function readPurchaseSnapshot(): YmPurchaseSnapshot | null {
    try {
        const raw = sessionStorage.getItem(YM_PURCHASE_SNAPSHOT_KEY) || localStorage.getItem(YM_PURCHASE_SNAPSHOT_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as YmPurchaseSnapshot;
        if (!parsed || typeof parsed !== 'object') return null;
        if (parsed.v !== 1 && parsed.v !== 2) return null;
        if (!Array.isArray(parsed.products)) return null;
        return parsed;
    } catch {
        return null;
    }
}
function normalizeProductIds(value: unknown): string[] {
    if (!Array.isArray(value)) return [];
    return Array.from(new Set(value.map((item) => String(item ?? '').trim()).filter(Boolean)));
}
function getCleanupKey(purchaseId: string): string {
    return `cart_paid_cleanup_done:${purchaseId}`;
}
function hasCleanupDone(purchaseId: string): boolean {
    try {
        return localStorage.getItem(getCleanupKey(purchaseId)) === '1';
    } catch {
        return false;
    }
}
function markCleanupDone(purchaseId: string): void {
    try {
        localStorage.setItem(getCleanupKey(purchaseId), '1');
    } catch {
        // ignore
    }
}
function resolvePurchaseId(snapshot: YmPurchaseSnapshot | null): string {
    if (!snapshot) return '';
    return String(snapshot.paymentId || snapshot.orderNumber || '').trim();
}
function resolveSnapshotUserId(snapshot: YmPurchaseSnapshot | null): string {
    if (!snapshot) return '';
    return String(snapshot.userId || '').trim();
}
function shouldSkipSnapshotForCurrentUser(snapshot: YmPurchaseSnapshot | null, currentUserId: string): boolean {
    const snapshotUserId = resolveSnapshotUserId(snapshot);
    if (!snapshotUserId) return false;
    if (!currentUserId) return true;
    return snapshotUserId !== currentUserId;
}
function readLocalCart(): CartLikeItem[] {
    try {
        const raw = localStorage.getItem(LOCAL_CART_STORAGE_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw) as unknown;
        return Array.isArray(parsed) ? (parsed as CartLikeItem[]) : [];
    } catch {
        return [];
    }
}
function writeLocalCart(cart: CartLikeItem[]): void {
    try {
        localStorage.setItem(LOCAL_CART_STORAGE_KEY, JSON.stringify(cart));
    } catch {
        // ignore
    }
}
function filterCartItemsByProductIds<T extends CartLikeItem>(cart: T[], productIds: string[]): T[] {
    const ids = new Set(normalizeProductIds(productIds));
    if (!ids.size) return cart.slice();
    return cart.filter((item) => {
        const productId = String(item?.productId ?? '').trim();
        return !productId || !ids.has(productId);
    });
}
function removePaidItemsFromLocalCart(productIds: string[]): boolean {
    const currentCart = readLocalCart();
    if (!currentCart.length) return false;
    const nextCart = filterCartItemsByProductIds(currentCart, productIds);
    const changed = nextCart.length !== currentCart.length;
    if (changed) {
        writeLocalCart(nextCart);
    }
    return changed;
}
function isPaidLikeOrderStatus(value: unknown): boolean {
    const status = String(value ?? '')
        .trim()
        .toLowerCase();
    return status === 'paid' || status === 'assembled' || status === 'completed';
}

async function cleanupServerCart(productIds: string[], signal: AbortSignal): Promise<'server' | 'fallback-local'> {
    const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/auth/user/cart`, {
        method: 'GET',
        credentials: 'include',
        signal,
    });
    if (response.status === 401 || response.status === 403) {
        return 'fallback-local';
    }
    if (!response.ok) {
        throw new Error(`Не удалось получить корзину пользователя: ${response.status}`);
    }
    const data = (await response.json().catch(() => ({}))) as UserCartResponse;
    const currentCart = Array.isArray(data?.cart) ? (data.cart as CartLikeItem[]) : [];
    const nextCart = filterCartItemsByProductIds(currentCart, productIds);
    if (nextCart.length === currentCart.length) {
        return 'server';
    }
    const csrfToken = await getCsrfToken();
    const patchResponse = await fetch(`${process.env.REACT_APP_SERVER_URL}/auth/user/cart`, {
        method: 'PATCH',
        credentials: 'include',
        signal,
        headers: {
            'Content-Type': 'application/json',
            'csrf-token': csrfToken,
        },
        body: JSON.stringify({ cart: nextCart }),
    });
    if (!patchResponse.ok) {
        throw new Error(`Не удалось обновить корзину пользователя: ${patchResponse.status}`);
    }
    return 'server';
}

export function usePaidOrderCartReconcile(): void {
    const dispatch = useDispatch<AppDispatch>();
    const currentAuthUserId = useSelector((s: RootState) => {
        const user = s.auth.user;
        if (!user || typeof user !== 'object') return '';
        const record = user as Record<string, unknown>;
        const id = typeof record._id === 'string' ? record._id : typeof record.id === 'string' ? record.id : '';
        return id.trim();
    });
    const inFlightRef = useRef(false);
    const abortRef = useRef<AbortController | null>(null);
    const reconcile = useCallback(async () => {
        if (inFlightRef.current) return;
        if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
        const snapshot = readPurchaseSnapshot();
        if (!snapshot) return;
        if (shouldSkipSnapshotForCurrentUser(snapshot, currentAuthUserId)) return;
        const purchaseId = resolvePurchaseId(snapshot);
        if (!purchaseId) return;
        if (hasCleanupDone(purchaseId)) return;
        const paymentId = String(snapshot.paymentId || '').trim();
        if (!paymentId) return;
        const productIds = normalizeProductIds(snapshot.productIds);
        if (!productIds.length) return;
        inFlightRef.current = true;
        const ctrl = new AbortController();
        abortRef.current = ctrl;
        try {
            const orderResponse = await fetch(`${process.env.REACT_APP_SERVER_URL}/orders/by-payment/${paymentId}`, {
                method: 'GET',
                credentials: 'include',
                signal: ctrl.signal,
            });
            if (!orderResponse.ok) {
                return;
            }
            const orderData = (await orderResponse.json().catch(() => null)) as { orderStatus?: unknown } | null;
            if (!isPaidLikeOrderStatus(orderData?.orderStatus)) {
                return;
            }
            const cleanupMode = await cleanupServerCart(productIds, ctrl.signal);
            if (cleanupMode === 'fallback-local') {
                removePaidItemsFromLocalCart(productIds);
            }
            for (const productId of productIds) {
                dispatch(removeProductFromCart(productId));
            }
            markCleanupDone(purchaseId);
        } catch (error) {
            const e = error as Error | undefined;
            if (e?.name === 'AbortError') return;
            console.error('usePaidOrderCartReconcile: reconcile failed', error);
        } finally {
            abortRef.current = null;
            inFlightRef.current = false;
        }
    }, [dispatch, currentAuthUserId]);

    useEffect(() => {
        void reconcile();
        const onFocus = () => {
            void reconcile();
        };
        const onOnline = () => {
            void reconcile();
        };
        const onVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                void reconcile();
            }
        };
        window.addEventListener('focus', onFocus);
        window.addEventListener('online', onOnline);
        document.addEventListener('visibilitychange', onVisibilityChange);
        const intervalId = window.setInterval(() => {
            void reconcile();
        }, RECONCILE_INTERVAL_MS);
        return () => {
            window.removeEventListener('focus', onFocus);
            window.removeEventListener('online', onOnline);
            document.removeEventListener('visibilitychange', onVisibilityChange);
            window.clearInterval(intervalId);
            abortRef.current?.abort();
        };
    }, [reconcile]);
}

export default usePaidOrderCartReconcile;
